/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import {
  useForm,
  FieldValues,
  SubmitHandler,
  Controller
} from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';

import Button from 'components/base/Button';
import { showAlert } from 'store/alert/alert.thunk';
import customersService from 'service/customer';
import PhoneNumberInput from 'components/forms/PhoneNumberInput';
import useCountryUnicode from 'hooks/useCountryUnicode';

interface AddNewContactProps {
  refetch: any;
  setIsOpenNewContact: (show: boolean) => void;
}

const AddNewContact = ({
  refetch,
  setIsOpenNewContact
}: AddNewContactProps) => {
  const { t } = useTranslation();
  const dispatch: Dispatch<any> = useDispatch();
  const inputRef = useRef<HTMLInputElement | any>();
  const unicode = useCountryUnicode();

  const [loading, setLoading] = useState(false);

  const genderOption = [
    {
      value: 'MALE',
      label: t('male')
    },
    {
      value: 'FEMALE',
      label: t('female')
    }
  ];

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm();

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
      setTimeout(() => inputRef.current?.select(), 300);
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = data => {
    setLoading(true);
    const createdData = {
      first_name: data?.first_name,
      is_master: false,
      last_name: data?.last_name,
      phone_number: data?.phone_number,
      gender: data?.gender
    };

    customersService.create(createdData).then((res: any) => {
      if (res) {
        dispatch(
          showAlert({
            title: `${t('contact')} ${t('successfully_added')}`,
            type: 'success'
          })
        );
        setLoading(false);
        refetch();
        setIsOpenNewContact(false);
      }
    });
  };

  useEffect(() => {
    focusInput();
  }, []);

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <Row className="mb-2">
        <Col>
          <Controller
            name={`first_name`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Floating className="md-4">
                <Form.Control
                  {...field}
                  ref={inputRef}
                  type="text"
                  placeholder={t('name')}
                  onFocus={e => e.target.select()}
                  isInvalid={!!errors.first_name}
                />
                <label htmlFor="floatingInputCustom">{t('name')}</label>
                {errors.first_name && (
                  <Form.Control.Feedback type="invalid">
                    {t('required_field')}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            )}
          />
        </Col>
      </Row>
      <Row className="mb-2">
        <Col>
          <Controller
            name={`last_name`}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Floating className="md-4">
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={t('second_name')}
                  onFocus={e => e.target.select()}
                  isInvalid={!!errors.last_name}
                />
                <label htmlFor="floatingInputCustom">{t('second_name')}</label>
                {errors.last_name && (
                  <Form.Control.Feedback type="invalid">
                    {t('required_field')}
                  </Form.Control.Feedback>
                )}
              </Form.Floating>
            )}
          />
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <div className="form-icon-container">
            <Controller
              name="phone_number"
              control={control}
              rules={{
                required: true
              }}
              defaultValue=""
              render={({ field }) => (
                <PhoneNumberInput
                  field={field}
                  unicode={unicode}
                  isInvalid={!!errors.phone_number}
                />
              )}
            />
            {errors?.phone_number && (
              <span
                style={{ marginTop: '2px', color: 'red', fontSize: '12px' }}
              >
                {t('required_field')}
              </span>
            )}
          </div>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <div className="react-select-container">
            <Controller
              name="gender"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Form.Floating>
                  <Form.Select {...field}>
                    <option className="d-none" value=""></option>
                    {genderOption?.map((option: any) => (
                      <option
                        key={option.value}
                        value={option.value}
                        className="option"
                      >
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>

                  <label htmlFor="floatingInputCustom">{t('gender')}</label>
                </Form.Floating>
              )}
            />
          </div>
        </Col>
      </Row>

      <Button
        style={{ height: '48px', marginTop: 'auto' }}
        variant="primary"
        type="submit"
        loading={loading}
        loadingPosition="start"
      >
        {t('add')}
      </Button>
    </Form>
  );
};

export default AddNewContact;
